import { create } from 'zustand';
import { persist, createJSONStorage, StateStorage } from 'zustand/middleware';

const getSafeStorage = (): StateStorage => {
  try {
    const testKey = '__test__';
    window.localStorage.setItem(testKey, testKey);
    window.localStorage.removeItem(testKey);
    return localStorage;
  } catch (e) {
    console.warn(
      'Storage is disabled or unavailable. Falling back to in-memory storage.',
    );
    const memoryStorage = new Map<string, string>();
    return {
      getItem: (name) => memoryStorage.get(name) ?? null,
      setItem: (name, value) => memoryStorage.set(name, value),
      removeItem: (name) => memoryStorage.delete(name),
    };
  }
};

export type BookmarkType = 'event' | 'article';

// type `null` = type unknown (e.g. from legacy bookmarks of the v0→v1
// migration). Such IDs go into both buckets (eventIds + articleIds) on the
// bookmark request so the backend can resolve them despite the lost type.
export interface Bookmark {
  id: string;
  type: BookmarkType | null;
}

interface BookmarkState {
  bookmarks: Bookmark[];
  addBookmark: (id: string, type?: BookmarkType) => void;
  removeBookmark: (id: string) => void;
  _hasHydrated: boolean;
  setHasHydrated: (state: boolean) => void;
  storageError: boolean;
}

export const useBookmarkStore = create<BookmarkState>()(
  persist(
    (set) => ({
      bookmarks: [],
      _hasHydrated: false,
      storageError: false,

      setHasHydrated: (state) => set({ _hasHydrated: state }),
      addBookmark: (id, type) =>
        set((state) => ({
          bookmarks: state.bookmarks.some((bookmark) => bookmark.id === id)
            ? state.bookmarks
            : [...state.bookmarks, { id, type: type ?? null }],
        })),
      removeBookmark: (id) =>
        set((state) => ({
          bookmarks: state.bookmarks.filter((bookmark) => bookmark.id !== id),
        })),
    }),
    {
      name: 'bookmark-storage',
      version: 2,
      storage: createJSONStorage(() => getSafeStorage()),
      migrate: (persistedState, version) => {
        let state = persistedState as Partial<BookmarkState>;

        // v0 → v1: v0 stored a flat `bookmarkedIds: string[]` list without a
        // type. Those IDs are taken over as type-less bookmarks.
        if (version < 1 && state) {
          const legacy = state as { bookmarkedIds?: string[] };
          state = {
            bookmarks: (legacy.bookmarkedIds ?? []).map((id) => ({ id, type: null })),
          };
        }

        // v1 → v2: content IDs now carry the prefix `cnt_` (HIH-1128).
        // Already-stored bookmarks still reference the old IDs without the
        // prefix → migrate them to the new IDs, otherwise the backend no longer
        // finds them and the bookmark state is wrong.
        if (version < 2 && state?.bookmarks) {
          state = {
            ...state,
            bookmarks: state.bookmarks.map((bookmark) => ({
              ...bookmark,
              id: bookmark.id.startsWith('cnt_') ? bookmark.id : `cnt_${bookmark.id}`,
            })),
          };
        }

        return state;
      },
      onRehydrateStorage: () => (state, error) => {
        if (error || !state) {
          useBookmarkStore.setState({
            _hasHydrated: true,
            storageError: true,
          });
        } else {
          state.setHasHydrated(true);
        }
      },
    },
  ),
);

// Re-sync store from localStorage when page is restored from bfcache
if (typeof window !== 'undefined') {
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      useBookmarkStore.persist.rehydrate();
    }
  });
}
